import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Dialog, DialogTitle, Divider, Tab, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from "classnames";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import AddEditDashboard from "../../atoms/AddEditDashboard";
import BulkAddDashboards from "../../atoms/BulkAddDashboards";

export enum AddEditCompany {
    Single = 'Single company',
    Bulk = 'By CSV',
}

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            height: 'fit-content',
            borderRadius: '16px',
            maxWidth: '672px',
            width: '100%',
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '32px 32px 24px',
        borderBottom: '1px solid #e2e8f0',
        fontFamily: 'Inter',
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#0f172a',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        height: '100%',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 0,
    },
    block: {
        width: '100%',
        height: 'fit-content',
        padding: '12px 16px',
        borderRadius: 16,
        background: 'rgba(240,254,254,0.7)',
        border: `2px solid ${theme.colors.primary['100']}`,
        boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        transition: '0.5s ease-in-out',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem !important',
        fontWeight: '700 !important',
        color: theme.palette.primary.main,
    },
    createButton: {
        height: 36,
        minWidth: 72,
        borderRadius: '24px !important',
        borderWidth: 3,
        fontFamily: 'Lato',
        fontSize: '0.9rem !important',
        textTransform: 'none !important' as any,
        fontWeight: 'bold !important',
        "&:hover": {
            borderWidth: 3,
            fontWeight: 'bold !important',
        },
    },
    success: {
        background: theme.colors.success[600],
        "&:hover, &:disabled": {
            background: `${theme.colors.success[600]} !important`,
        },
        "& > svg": {
            fill: 'white !important',
        }
    },
    failed: {
        background: theme.colors.error[600],
        "&:hover, &:disabled": {
            background: `${theme.colors.error[600]} !important`,
        },
        "& > svg": {
            fill: 'white !important',
        }
    },
    blocked: {
        "&:hover, &:disabled": {
            cursor: 'not-allowed !important',
        },
        cursor: 'not-allowed !important',
    },
    label: {
        minWidth: 80,
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: 'black',
    },
    link: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        textDecoration: 'none',
        "&:hover": {
            textDecoration: 'none !important',
        },
        cursor: 'pointer',
    },
    caption: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        color: theme.colors.neutral['500'],
    },
    commonButton: {
        minWidth: 80,
        borderRadius: 32,
        textTransform: 'none',
        fontWeight: 'bold',
    },
    cancelButton: {
        width: 100,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        backgroundColor: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    tabs: {
        borderBottom: '1px solid #e2e8f0',
        padding: '8px 32px 0 32px',
        "& .MuiTabs-indicator": {
            height: 3,
            background: '#0d9488',
        }
    },
    tab: {
      textTransform: 'none',
      fontWeight: 600,
      padding: '12px 24px',
    },
    tabText: {
        color: '#64748b',
        fontFamily: 'Lato',
        fontSize: '14px',
        minWidth: 'auto',
        fontWeight: '600',
        textTransform: 'none',
    },
    selectedTabText: {
        color: '#0d9488',
        fontFamily: 'Lato',
        fontWeight: '600',
        textTransform: 'none',
    },
    tabPanel: {
        height: '100%',
        width: '100%',
        padding: 'unset',
    },
}));

const AddEditCompanyModal: React.FC<{
    isOpen: boolean,
    dashboardId?: string,
    collectionKey?: string,
    onClose: () => void,
}> = ({ isOpen, dashboardId, collectionKey, onClose }) => {
    const classes = useStyles();
    const { dashboards } = useContext(DashboardsContext);
    const [selectedTab, setSelectedTab] = useState<AddEditCompany>(AddEditCompany.Single);

    const dashboard = useMemo(() => dashboards.find(d => d.id === dashboardId), [dashboardId, dashboards]);

    const getClassForText = useCallback((tab: AddEditCompany) =>
        (classNames(classes.tabText, selectedTab === tab && classes.selectedTabText))
        // eslint-disable-next-line
    , [selectedTab]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={onClose}>
            <DialogTitle className={classes.dialogTitle}>
                {!dashboard ? 'Add a ' : 'Edit existing '}
                {'company...'}
            </DialogTitle>
            <TabContext value={selectedTab}>
                <TabList className={classes.tabs} onChange={(_, value) => setSelectedTab(value)}>
                    <Tab className={classes.tab}
                        label={<Typography className={getClassForText(AddEditCompany.Single)}>
                                {AddEditCompany.Single}
                            </Typography>}
                        value={AddEditCompany.Single} />
                    <Tab className={classes.tab}
                        label={<Typography className={getClassForText(AddEditCompany.Bulk)}>
                                    {AddEditCompany.Bulk}
                                </Typography>}
                        value={AddEditCompany.Bulk} />
                </TabList>
            </TabContext>
            <Divider />
            <TabContext value={selectedTab}>
                <TabPanel className={classes.tabPanel} value={AddEditCompany.Single}>
                    <AddEditDashboard collectionKey={collectionKey} onClose={onClose} />
                </TabPanel>
                <TabPanel className={classes.tabPanel} value={AddEditCompany.Bulk}>
                    <BulkAddDashboards onClose={onClose} />
                </TabPanel>
            </TabContext>
        </Dialog>
    </>);
}

export default AddEditCompanyModal;